import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useChatStore, useToastStore } from '@/store';
import socketManager from '@/lib/socket';
import { CHAT_INPUT_STYLE } from '@/constants/config';
import ChatInput from '@/components/ChatInput/ChatInput';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

export default function DeckNewChatComposer({ deckSlug, columnId, onConversationStarted, onFocus }) {
  const { colors } = useTheme();
  const userId = useAuthStore(state => state.user?.id);
  const createConversation = useChatStore(state => state.createConversation);
  const addToast = useToastStore(state => state.actions.addToast);
  const pendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const contextId = `new-deck-${deckSlug}-${columnId}`;
  const voiceModeTarget = useMemo(() => ({ deckSlug, columnId }), [deckSlug, columnId]);

  const handleSend = useCallback(async formattedMessage => {
    if (!formattedMessage || pendingRef.current) return false;
    pendingRef.current = true;
    setIsSending(true);
    try {
      if (!(await socketManager.requireConnection())) return false;
      const conversation = await createConversation({ userId, formattedMessage });
      if (!conversation?.id) throw new Error('Failed to create conversation');
      // The new conversation must receive its kick even after switching decks.
      setTimeout(() => socketManager.emitKickConversation(userId, conversation.id), 250);
      onConversationStarted(conversation.id);
      return true;
    } catch (error) {
      addToast(error.message || 'Failed to create conversation', 'error');
      return false;
    } finally {
      pendingRef.current = false;
      setIsSending(false);
    }
  }, [createConversation, userId, onConversationStarted, addToast]);

  return <View style={styles.container}>
    <View style={styles.body}>
      <Icon name="message" size={28} color={colors.text_color_secondary} />
      <Text size="l" weight="semibold" style={styles.label}>Start a conversation</Text>
      <Text size="s" color="secondary" style={styles.label}>Choose a model, type a message, or use your voice.</Text>
    </View>
    <ChatInput
      conversationId={contextId}
      voiceModeTarget={voiceModeTarget}
      forceInputStyle={CHAT_INPUT_STYLE.FIXED}
      onSend={handleSend}
      onFocus={onFocus}
      placeholder="Type a message…"
      forcePlaceholder
      isLoading={isSending}
      isDisabled={isSending}
      withModels
      isCompact
      autoFocus
    />
  </View>;
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  body: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24, gap: 12 },
  label: { textAlign: 'center', lineHeight: 24 },
});
